/**
 * AircraftDetailCard Component
 *
 * Shared aircraft detail popup used in both 2D (Leaflet) and 3D (Cesium) map views.
 * Shows aircraft photo (from airport-data.com), identity, telemetry, and status.
 *
 * Premium feature — only shown for aircraft tracking subscribers.
 */

import { useAircraftPhoto } from '../hooks/useAircraftPhoto';
import type { AircraftState } from '../types/events';
import type { InterpolatedAircraft } from '../utils/aircraftInterpolation';
import './AircraftDetailCard.css';

// Compass direction labels
function headingToCompass(deg: number): string {
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return dirs[Math.round(((deg % 360) + 360) % 360 / 22.5) % 16];
}

interface AircraftDetailCardProps {
  /** Aircraft data — accepts both raw AircraftState and InterpolatedAircraft */
  aircraft: AircraftState | InterpolatedAircraft;
  /** Called when user clicks close button (3D mode only — Leaflet handles its own close) */
  onClose?: () => void;
  /** Show "Follow Aircraft" button (3D mode only) */
  showFollowButton?: boolean;
  /** Whether aircraft is currently being followed */
  isFollowing?: boolean;
  /** Called when user clicks Follow/Unfollow */
  onFollow?: (icao24: string) => void;
  /** Called when user clicks Unfollow */
  onUnfollow?: () => void;
  /** Called when user clicks "Ride Along" (3D simulator mode) */
  onRideAlong?: (icao24: string) => void;
}

export default function AircraftDetailCard({
  aircraft,
  onClose,
  showFollowButton = false,
  isFollowing = false,
  onFollow,
  onUnfollow,
  onRideAlong,
}: AircraftDetailCardProps) {
  const ac = aircraft;
  const { photoUrl, photographer, isLoading: photoLoading } = useAircraftPhoto(ac.icao24);

  // Derive display values
  const altitudeFt = ac.altitude_ft ?? (ac.altitude_m ? Math.round(ac.altitude_m * 3.28084) : null);
  const speedKmh = ac.velocity_ms ? Math.round(ac.velocity_ms * 3.6) : null;
  const speedKts = ac.speed_kts ?? (ac.velocity_ms ? Math.round(ac.velocity_ms * 1.944) : null);
  const heading = 'interpHeading' in ac ? ac.interpHeading : ac.heading;
  const isEmergency = ac.is_emergency ?? false;
  const isStale = 'isStale' in ac ? ac.isStale : false;
  const verticalRateFpm = ac.vertical_rate ? Math.round(ac.vertical_rate * 196.85) : null;

  const hasType = ac.manufacturer || ac.model;
  const hasBadges = isEmergency || ac.is_helicopter || ac.on_ground || isStale;

  return (
    <div className="aircraft-detail-card">
      {/* Photo section */}
      <div className="aircraft-photo-container">
        {photoLoading ? (
          <div className="aircraft-photo-skeleton" />
        ) : photoUrl ? (
          <>
            <img
              className="aircraft-photo"
              src={photoUrl}
              alt={`${ac.manufacturer || ''} ${ac.model || ac.registration || ac.icao24}`.trim()}
              loading="lazy"
            />
            {photographer && (
              <span className="aircraft-photo-credit">{photographer}</span>
            )}
          </>
        ) : (
          <div className="aircraft-photo-placeholder">
            {ac.is_helicopter ? (
              <svg viewBox="0 0 32 32" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <line x1="4" y1="12" x2="28" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                <ellipse cx="16" cy="14" rx="5" ry="8" />
                <ellipse cx="16" cy="7" rx="3" ry="4" />
                <rect x="14" y="20" width="4" height="8" />
                <line x1="10" y1="27" x2="22" y2="27" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z"/>
              </svg>
            )}
            <span className="aircraft-photo-placeholder-text">
              {hasType ? `${ac.manufacturer || ''} ${ac.model || ''}`.trim() : 'No photo available'}
            </span>
          </div>
        )}
      </div>

      {/* Header / Identity */}
      <div className="aircraft-detail-header">
        <span className="aircraft-detail-icon">
          {ac.is_helicopter ? '\uD83D\uDE81' : '\u2708\uFE0F'}
        </span>
        <div className="aircraft-detail-identity">
          <div className="aircraft-detail-callsign">
            {ac.callsign || ac.registration || 'Unknown'}
          </div>
          <div className="aircraft-detail-secondary-id">
            {ac.icao24.toUpperCase()}
            {ac.registration && ac.callsign && ac.registration !== ac.callsign && (
              <> &bull; {ac.registration}</>
            )}
          </div>
        </div>
        {onClose && (
          <button className="aircraft-detail-close" onClick={onClose} title="Close">
            &times;
          </button>
        )}
      </div>

      {/* Follow + Ride Along buttons rendered HIGH in the card so the
          primary CTA is visible without scrolling — the popup max-height
          is 420 px and the photo + telemetry below would otherwise push
          this row below the fold. Renders if any of the action props are
          wired up; the 2D popup only has onRideAlong, the 3D popup has
          all three. */}
      {showFollowButton && (onFollow || onUnfollow || onRideAlong) && (
        <div className="aircraft-detail-actions">
          {(onFollow || onUnfollow) && (
            <button
              className={`aircraft-follow-btn ${isFollowing ? 'unfollow' : 'follow'}`}
              onClick={() => {
                if (isFollowing) {
                  onUnfollow?.();
                } else {
                  onFollow?.(ac.icao24);
                }
              }}
            >
              {isFollowing ? (
                <>Stop Following</>
              ) : (
                <>Follow Aircraft</>
              )}
            </button>
          )}
          {onRideAlong && !ac.on_ground && (
            <button
              className="aircraft-ride-along-btn"
              onClick={() => onRideAlong(ac.icao24)}
            >
              Ride Along &mdash; Simulator Mode
            </button>
          )}
        </div>
      )}

      {/* Aircraft type */}
      {hasType && (
        <div className="aircraft-detail-type">
          <div className="aircraft-detail-type-name">
            {ac.manufacturer && ac.model
              ? `${ac.manufacturer} ${ac.model}`
              : ac.model || ac.manufacturer}
          </div>
          {ac.operator && (
            <div className="aircraft-detail-operator">
              Operator: {ac.operator}
            </div>
          )}
          {ac.type_code && (
            <span className="aircraft-detail-typecode">{ac.type_code}</span>
          )}
        </div>
      )}

      {/* Telemetry grid */}
      <div className="aircraft-detail-telemetry">
        {altitudeFt !== null && (
          <div className="aircraft-telemetry-item">
            <span className="aircraft-telemetry-label">Altitude</span>
            <span className="aircraft-telemetry-value">
              {altitudeFt.toLocaleString()}
              <span className="aircraft-telemetry-unit"> ft</span>
            </span>
          </div>
        )}
        {speedKmh !== null && (
          <div className="aircraft-telemetry-item">
            <span className="aircraft-telemetry-label">Speed</span>
            <span className="aircraft-telemetry-value">
              {speedKmh}
              <span className="aircraft-telemetry-unit"> km/h</span>
              {speedKts !== null && (
                <span className="aircraft-telemetry-unit"> ({speedKts} kts)</span>
              )}
            </span>
          </div>
        )}
        {heading !== null && heading !== undefined && (
          <div className="aircraft-telemetry-item">
            <span className="aircraft-telemetry-label">Heading</span>
            <span className="aircraft-telemetry-value">
              {Math.round(heading)}&deg;
              <span className="aircraft-telemetry-unit"> {headingToCompass(heading)}</span>
            </span>
          </div>
        )}
        {verticalRateFpm !== null && verticalRateFpm !== 0 && (
          <div className="aircraft-telemetry-item">
            <span className="aircraft-telemetry-label">Vertical</span>
            <span className="aircraft-telemetry-value">
              {verticalRateFpm > 0 ? '\u2191' : '\u2193'}
              {Math.abs(verticalRateFpm).toLocaleString()}
              <span className="aircraft-telemetry-unit"> fpm</span>
            </span>
          </div>
        )}
        {ac.origin_country && (
          <div className="aircraft-telemetry-item">
            <span className="aircraft-telemetry-label">Country</span>
            <span className="aircraft-telemetry-value">{ac.origin_country}</span>
          </div>
        )}
      </div>

      {/* Status badges */}
      {hasBadges && (
        <div className="aircraft-detail-badges">
          {isEmergency && (
            <div className="aircraft-badge aircraft-badge-emergency">
              Emergency / Firefighting Aircraft
            </div>
          )}
          {ac.is_helicopter && !isEmergency && (
            <div className="aircraft-badge aircraft-badge-helicopter">
              Helicopter — possible emergency/firefighting aircraft
            </div>
          )}
          {ac.on_ground && (
            <div className="aircraft-badge aircraft-badge-ground">On ground</div>
          )}
          {isStale && (
            <div className="aircraft-badge aircraft-badge-stale">
              Signal lost — showing last known position
            </div>
          )}
        </div>
      )}

    </div>
  );
}
